/**
 * The receiving end of a share link (`D258`, `D260`): `go.mypocketgym.app/
 * ?ref=<id>` opens the site, and this is the whole of what `ref` does.
 *
 * ── READ ONCE, BEFORE THE ROUTER, AND THEN GONE FROM THE URL ───────────────
 * {@link captureShareLanding} runs in `main.tsx`'s boot, before the first
 * render: it keeps the ref on the device, reports `link_opened`, and takes
 * `ref` out of the address with `replaceState`. The search schema would strip
 * it anyway (`app/search.ts`); taking it out first means a reload does not
 * report the same landing twice. `ref` opens nothing and generates nothing —
 * it is not a door like `sheet=draft` (`T-145`).
 *
 * ── SENT WITH THE CODE, CLEARED AFTER ──────────────────────────────────────
 * {@link submitCodeWithRef} hands the kept ref to the sign-in's verify, where
 * a NEW account turns it into `signup_from_link`, and clears it once a code
 * has been accepted — a later account on the same device did not come from
 * that link.
 *
 * The native shell never boots with `?ref=` (its deep links carry two keys,
 * `D96`), so on the phone this finds nothing; the links land on the site.
 */
import type { ApiClient } from "@repo/client/api";
import { preferences } from "../../native";
import { landingRef, liveRef, withoutRef } from "./link";
import { reportLinkOpened } from "./report";

export const SHARE_REF_KEY = "mypocketgym.share.landing-ref";

export function captureShareLanding(
  location: Pick<Location, "hash" | "pathname" | "search"> = window.location,
  history: Pick<History, "replaceState" | "state"> = window.history,
  now: () => number = Date.now
): void {
  const ref = landingRef(location.search);
  if (ref === null) {
    return;
  }
  history.replaceState(
    history.state,
    "",
    `${location.pathname}${withoutRef(location.search)}${location.hash}`
  );
  preferences
    .set(SHARE_REF_KEY, JSON.stringify({ at: now(), ref }))
    .catch(() => undefined);
  reportLinkOpened(ref);
}

/** `ApiClient.submitCode`, carrying the landing's ref while it is live. */
export async function submitCodeWithRef(
  api: Pick<ApiClient, "submitCode">,
  verificationId: string,
  code: string
): Promise<{ accountId: string; isNewAccount: boolean }> {
  const ref = liveRef(
    await preferences.get(SHARE_REF_KEY).catch(() => null),
    Date.now()
  );
  const result = await api.submitCode(verificationId, code, ref ?? undefined);
  if (ref !== null) {
    preferences.remove(SHARE_REF_KEY).catch(() => undefined);
  }
  return result;
}
